import React from "react";

import SecurityIcon from "@mui/icons-material/Security";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import { sectionLabelSx } from "src/shell/styles/themeUtils";

import { UserSettings } from "./settings-helpers";

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface SecuritySettingsProps {
  settings: UserSettings;
  onSecurityChange: (
    key: keyof UserSettings["security"],
    value: boolean | number,
  ) => void;
  passwordDialogOpen: boolean;
  onPasswordDialogClose: () => void;
  passwordForm: PasswordForm;
  onPasswordFormChange: (form: PasswordForm) => void;
  onChangePassword: () => void;
}

const SecuritySettings: React.FC<SecuritySettingsProps> = ({
  settings,
  onSecurityChange,
  passwordDialogOpen,
  onPasswordDialogClose,
  passwordForm,
  onPasswordFormChange,
  onChangePassword,
}) => {
  const theme = useTheme();

  return (
    <>
      {/* Security */}
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 2,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <SecurityIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="subtitle2" sx={sectionLabelSx}>
            Security
          </Typography>
        </Stack>

        <Stack spacing={2.5}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.security.twoFactorEnabled}
                onChange={(e) =>
                  onSecurityChange("twoFactorEnabled", e.target.checked)
                }
                color="primary"
                size="small"
              />
            }
            label={
              <Box sx={{ ml: 0.5 }}>
                <Typography variant="body2" fontWeight={500}>
                  Two-factor authentication
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Add an extra layer of security to your account
                </Typography>
              </Box>
            }
          />
          <Divider />
          <FormControlLabel
            control={
              <Switch
                checked={
                  !!settings.security.sessionTimeout &&
                  settings.security.sessionTimeout > 0
                }
                onChange={(e) =>
                  onSecurityChange("sessionTimeout", e.target.checked ? 30 : 0)
                }
                color="primary"
                size="small"
              />
            }
            label={
              <Box sx={{ ml: 0.5 }}>
                <Typography variant="body2" fontWeight={500}>
                  Session timeout
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {settings.security.sessionTimeout > 0
                    ? `${settings.security.sessionTimeout} minutes`
                    : "No timeout"}
                </Typography>
              </Box>
            }
          />
          <Divider />
          <FormControlLabel
            control={
              <Switch
                checked={
                  !!settings.security.passwordExpiryDays &&
                  settings.security.passwordExpiryDays > 0
                }
                onChange={(e) =>
                  onSecurityChange(
                    "passwordExpiryDays",
                    e.target.checked ? 90 : 0,
                  )
                }
                color="primary"
                size="small"
              />
            }
            label={
              <Box sx={{ ml: 0.5 }}>
                <Typography variant="body2" fontWeight={500}>
                  Password expiry
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {settings.security.passwordExpiryDays > 0
                    ? `${settings.security.passwordExpiryDays} days`
                    : "No expiry"}
                </Typography>
              </Box>
            }
          />
        </Stack>
      </Box>

      {/* Change Password Dialog */}
      <Dialog
        open={passwordDialogOpen}
        onClose={onPasswordDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              type="password"
              label="Current Password"
              value={passwordForm.currentPassword}
              onChange={(e) =>
                onPasswordFormChange({
                  ...passwordForm,
                  currentPassword: e.target.value,
                })
              }
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="password"
              label="New Password"
              value={passwordForm.newPassword}
              onChange={(e) =>
                onPasswordFormChange({
                  ...passwordForm,
                  newPassword: e.target.value,
                })
              }
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="password"
              label="Confirm New Password"
              value={passwordForm.confirmPassword}
              onChange={(e) =>
                onPasswordFormChange({
                  ...passwordForm,
                  confirmPassword: e.target.value,
                })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onPasswordDialogClose}>Cancel</Button>
          <Button onClick={onChangePassword} variant="contained">
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SecuritySettings;
